/**
 * The access list.
 *
 * Two kinds of account live here. A **directory** account is one the domain
 * already holds: the directory checks the password, this dialog decides
 * whether they may come in and what they may do. It is picked from a
 * directory-fed dropdown rather than typed, because a mistyped username would
 * create an entry matching nobody and the person would keep being turned away
 * with no clue why.
 *
 * A **local** account is one Proxmon owns outright — the password is hashed
 * into its own database. With no directory configured these are all the
 * accounts there are; with one, at least one local administrator is the way
 * back in on the morning the domain controller is the thing that is down.
 * That last one is protected from being demoted, suspended or removed.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type { DirectoryEntry, UserAccount, UserListResponse } from '@proxmon/shared';
import { MIN_PASSWORD_LENGTH } from '@proxmon/shared';
import { useStore } from '../state/store.ts';

/** Below this, a search box is clutter; above it, the dropdown is unusable. */
const SEARCH_THRESHOLD = 15;

export function UsersDialog({ onClose }: { onClose: () => void }) {
  const listUsers = useStore((s) => s.listUsers);
  const listDirectory = useStore((s) => s.listDirectory);
  const addUser = useStore((s) => s.addUser);
  const setUserRole = useStore((s) => s.setUserRole);
  const setUserEnabled = useStore((s) => s.setUserEnabled);
  const removeUser = useStore((s) => s.removeUser);
  const testDirectory = useStore((s) => s.testDirectory);
  const setUserPassword = useStore((s) => s.setUserPassword);
  const notify = useStore((s) => s.notify);
  const me = useStore((s) => s.session?.username);
  const ldapEnabled = useStore((s) => s.session?.ldapEnabled ?? false);

  const [data, setData] = useState<UserListResponse | null>(null);
  const [candidates, setCandidates] = useState<DirectoryEntry[] | null>(null);
  const [directoryReason, setDirectoryReason] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [search, setSearch] = useState('');
  const [pick, setPick] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'viewer'>('viewer');
  const [typedName, setTypedName] = useState('');
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  // Which kind of account the form below is adding. With no directory there is
  // only one answer, so the choice is not offered.
  const [mode, setMode] = useState<'ldap' | 'local'>(ldapEnabled ? 'ldap' : 'local');
  const [localPassword, setLocalPassword] = useState('');
  const [localDisplayName, setLocalDisplayName] = useState('');
  /** the local account whose password is being reset, inline under its row */
  const [resetFor, setResetFor] = useState<string | null>(null);

  useEffect(() => {
    if (!ldapEnabled) setMode('local');
  }, [ldapEnabled]);

  const fail = useCallback(
    (err: unknown, fallback: string) => notify('error', err instanceof Error ? err.message : fallback),
    [notify],
  );

  const reload = useCallback(async () => {
    try {
      setData(await listUsers());
    } catch (err) {
      fail(err, 'Could not read the access list.');
    }
    try {
      const directory = await listDirectory();
      if (directory.available) {
        setCandidates(directory.users);
        setTruncated(Boolean(directory.truncated));
        setDirectoryReason(null);
      } else {
        // Falling back to a typed username keeps the dialog usable while the
        // domain controller is down, instead of blocking on it.
        setCandidates(null);
        setDirectoryReason(directory.reason ?? 'unknown reason');
      }
    } catch (err) {
      setCandidates(null);
      setDirectoryReason(err instanceof Error ? err.message : 'unknown reason');
    }
  }, [listUsers, listDirectory, fail]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Matches on the display name as well as the login: no telling which of the
  // two the person doing the adding has in mind.
  const matches = useMemo(() => {
    if (!candidates) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return candidates;
    return candidates.filter(
      (c) => c.displayName.toLowerCase().includes(needle) || c.username.toLowerCase().includes(needle),
    );
  }, [candidates, search]);

  // One candidate left: preselect it rather than make them open the dropdown.
  useEffect(() => {
    if (matches.length === 1 && matches[0]) setPick(matches[0].username);
    else if (!matches.some((c) => c.username === pick)) setPick('');
  }, [matches, pick]);

  const run = async (action: () => Promise<void>, fallback: string) => {
    setBusy(true);
    try {
      await action();
      await reload();
    } catch (err) {
      fail(err, fallback);
    } finally {
      setBusy(false);
    }
  };

  const submitAdd = async (event: React.FormEvent) => {
    event.preventDefault();

    if (mode === 'local') {
      const username = typedName.trim();
      if (!username) {
        notify('error', 'Type a username.');
        return;
      }
      if (localPassword.length < MIN_PASSWORD_LENGTH) {
        notify('error', `The password needs at least ${MIN_PASSWORD_LENGTH} characters.`);
        return;
      }
      await run(async () => {
        await addUser({
          username,
          role: newRole,
          source: 'local',
          password: localPassword,
          displayName: localDisplayName.trim() || undefined,
        });
        setTypedName('');
        setLocalPassword('');
        setLocalDisplayName('');
      }, 'Could not create that account.');
      return;
    }

    const username = candidates ? pick : typedName.trim();
    if (!username) {
      notify('error', candidates ? 'Pick an account from the list.' : 'Type a username.');
      return;
    }
    const chosen = candidates?.find((c) => c.username === username);
    await run(async () => {
      // The directory's name and email travel with the row, so it reads as a
      // person straight away instead of a bare login until they first sign in.
      await addUser({
        username,
        role: newRole,
        source: 'ldap',
        displayName: chosen?.displayName,
        email: chosen?.email,
      });
      setPick('');
      setTypedName('');
      setSearch('');
    }, 'Could not add that account.');
  };

  const submitReset = async (username: string, password: string) => {
    if (password.length < MIN_PASSWORD_LENGTH) {
      notify('error', `The password needs at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    await run(async () => {
      await setUserPassword(username, password);
      setResetFor(null);
      notify('info', `Password changed for ${username}. Their other sessions are signed out.`);
    }, 'Could not change that password.');
  };

  const runTest = async () => {
    setTestResult('Testing…');
    try {
      const result = await testDirectory();
      setTestResult(
        result.ok
          ? `Directory reachable — ${result.server}, base ${result.baseDn}, ${result.sample} account(s) in the sample.`
          : `Failed: ${result.error ?? 'unknown error'}`,
      );
    } catch (err) {
      setTestResult(err instanceof Error ? err.message : 'Could not reach the server.');
    }
  };

  const users = data?.users ?? [];

  /*
   * The last enabled local administrator cannot be demoted, suspended or
   * removed — they are the way in when the directory is unreachable, and the
   * server refuses these operations anyway. Working it out here as well means
   * the controls are visibly locked rather than failing on click.
   */
  const localAdmins = users.filter((u) => u.source === 'local' && u.role === 'admin' && u.enabled);
  const isLastLocalAdmin = (user: UserAccount): boolean =>
    localAdmins.length === 1 && localAdmins[0]?.username === user.username;

  return (
    <div className="overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="dialog users-dialog" role="dialog" aria-modal="true" aria-label="Who may sign in">
        <header>
          <h2>Who may sign in</h2>
        </header>

        <div className="body">
          <p className="lede">
            {!ldapEnabled ? (
              <>
                No directory is configured, so these are local accounts: Proxmon stores the username, the
                role and a hashed password itself. <strong>Only the accounts below may sign in.</strong>
              </>
            ) : data?.autoCreate ? (
              <>
                The directory checks the password; rights are set here.{' '}
                <strong>Any directory account may sign in</strong> and is added as a viewer on first use.
              </>
            ) : (
              <>
                The directory checks the password; access and rights are decided here.{' '}
                <strong>Only the accounts below may sign in</strong> — being in the directory is not enough.
              </>
            )}
          </p>

          {testResult && <div className="hint test-result">{testResult}</div>}

          <table className="users-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Role</th>
                <th>Last seen</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan={4} className="empty">
                    Nobody is on the list yet — add someone below to let them in.
                  </td>
                </tr>
              )}
              {users.map((user) => (
                <Fragment key={user.username}>
                  <UserRow
                    user={user}
                    isMe={Boolean(me) && (user.displayName === me || user.username === me)}
                    busy={busy}
                    locked={isLastLocalAdmin(user)}
                    resetting={resetFor === user.username}
                    onRole={(role) => run(() => setUserRole(user.username, role), 'Could not change that role.')}
                    onEnabled={(enabled) =>
                      run(() => setUserEnabled(user.username, enabled), 'Could not change that account.')
                    }
                    onPassword={() => setResetFor(resetFor === user.username ? null : user.username)}
                    onRemove={() => {
                      const ok = window.confirm(
                        user.source === 'local'
                          ? `Delete the account ${user.username}?\n\nIt only exists in Proxmon, so this cannot be undone.`
                          : `Remove ${user.username}?\n\nTheir directory account is untouched, but they will no longer be able to sign in to Proxmon.`,
                      );
                      if (ok) void run(() => removeUser(user.username), 'Could not remove that account.');
                    }}
                  />
                  {resetFor === user.username && (
                    <PasswordRow
                      username={user.username}
                      busy={busy}
                      onCancel={() => setResetFor(null)}
                      onSubmit={(password) => void submitReset(user.username, password)}
                    />
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>

          <form className="add-user" onSubmit={submitAdd}>
            {ldapEnabled && (
              <div className="mode-switch" role="group" aria-label="What kind of account to add">
                <button
                  type="button"
                  className="btn small toggle"
                  aria-pressed={mode === 'ldap'}
                  disabled={busy}
                  onClick={() => setMode('ldap')}
                >
                  Directory account
                </button>
                <button
                  type="button"
                  className="btn small toggle"
                  aria-pressed={mode === 'local'}
                  disabled={busy}
                  onClick={() => setMode('local')}
                >
                  Local account
                </button>
              </div>
            )}

            {mode === 'ldap' && candidates && candidates.length > SEARCH_THRESHOLD && (
              <input
                className="search-field"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search a name or a username…"
                spellCheck={false}
                aria-label="Search the directory"
              />
            )}

            <div className="row">
              <label className="grow">
                <span>{mode === 'local' ? 'Username' : 'Add a directory account'}</span>
                {mode === 'ldap' && candidates ? (
                  <select value={pick} onChange={(event) => setPick(event.target.value)} disabled={busy}>
                    <option value="">
                      {matches.length === 0
                        ? 'No account matches'
                        : search
                          ? `— ${matches.length} match(es), pick one —`
                          : '— pick an account —'}
                    </option>
                    {matches.map((c) => (
                      <option key={c.username} value={c.username}>
                        {c.displayName ? `${c.displayName} (${c.username})` : c.username}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={typedName}
                    onChange={(event) => setTypedName(event.target.value)}
                    placeholder={mode === 'local' ? 'sulyvan' : 'directory username'}
                    spellCheck={false}
                    autoComplete="off"
                    disabled={busy}
                  />
                )}
              </label>

              <label>
                <span>Role</span>
                <select
                  value={newRole}
                  onChange={(event) => setNewRole(event.target.value as 'admin' | 'viewer')}
                  disabled={busy}
                >
                  <option value="viewer">Viewer</option>
                  <option value="admin">Administrator</option>
                </select>
              </label>

              {mode === 'ldap' && (
                <>
                  <button type="submit" className="btn primary" disabled={busy}>
                    Add
                  </button>
                  <button type="button" className="btn ghost" onClick={() => void runTest()} disabled={busy}>
                    Test directory
                  </button>
                </>
              )}
            </div>

            {mode === 'local' && (
              <div className="row">
                <label className="grow">
                  <span>Password</span>
                  <input
                    type="password"
                    value={localPassword}
                    onChange={(event) => setLocalPassword(event.target.value)}
                    autoComplete="new-password"
                    disabled={busy}
                  />
                </label>
                <label className="grow">
                  <span>Display name</span>
                  <input
                    value={localDisplayName}
                    onChange={(event) => setLocalDisplayName(event.target.value)}
                    placeholder="optional"
                    disabled={busy}
                  />
                </label>
                <button type="submit" className="btn primary" disabled={busy}>
                  Create
                </button>
              </div>
            )}

            <div className="hint">
              {mode === 'local' ? (
                <>
                  Proxmon stores this one itself: the username, the role, and the password as a scrypt hash.
                  At least {MIN_PASSWORD_LENGTH} characters. They can change it themselves once they are in.
                </>
              ) : directoryReason ? (
                <>
                  Directory unavailable ({directoryReason}) — type the username instead. It has to match the
                  directory exactly.
                </>
              ) : (
                <>
                  {candidates?.length ?? 0} directory account(s) not yet on the list
                  {truncated && <strong> — list truncated, the directory is larger than we read</strong>}. Adding
                  someone here is what lets them in.
                </>
              )}
            </div>
          </form>
        </div>

        <div className="foot">
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function UserRow({
  user,
  isMe,
  busy,
  locked,
  resetting,
  onRole,
  onEnabled,
  onPassword,
  onRemove,
}: {
  user: UserAccount;
  isMe: boolean;
  busy: boolean;
  /** the last enabled local admin: the controls that would lock us out are off */
  locked: boolean;
  resetting: boolean;
  onRole: (role: 'admin' | 'viewer') => void;
  onEnabled: (enabled: boolean) => void;
  onPassword: () => void;
  onRemove: () => void;
}) {
  const lockReason = 'The last local administrator — the way in when the directory is unreachable.';
  const local = user.source === 'local';

  return (
    <tr className={user.enabled ? undefined : 'disabled'}>
      <td>
        <strong>{user.displayName || user.username}</strong>
        <span className="sub">
          {user.username}
          {local && <em className="tag"> local</em>}
          {isMe && <em className="tag"> you</em>}
        </span>
      </td>
      <td>
        <select
          value={user.role}
          disabled={busy || user.roleLocked || locked}
          onChange={(event) => onRole(event.target.value as 'admin' | 'viewer')}
          aria-label={`Role for ${user.username}`}
        >
          <option value="viewer">Viewer</option>
          <option value="admin">Administrator</option>
        </select>
        {user.roleLocked && (
          <span className="tag lock" title="Pinned to admin under Settings > Directory; change it there">
            pinned
          </span>
        )}
        {locked && !user.roleLocked && (
          <span className="tag lock" title={lockReason}>
            break-glass
          </span>
        )}
      </td>
      <td className="dim">
        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'never'}
      </td>
      <td className="actions">
        {local && (
          <button
            type="button"
            className="btn ghost small"
            disabled={busy}
            aria-expanded={resetting}
            onClick={onPassword}
            title="Set a new password for this account"
          >
            Password
          </button>
        )}
        <button
          type="button"
          className="btn ghost small"
          disabled={busy || locked}
          onClick={() => onEnabled(!user.enabled)}
          title={locked ? lockReason : user.enabled ? 'Suspend this account' : 'Restore this account'}
        >
          {user.enabled ? 'Suspend' : 'Restore'}
        </button>
        <button
          type="button"
          className="btn ghost small danger"
          disabled={busy || locked}
          onClick={onRemove}
          title={locked ? lockReason : local ? 'Delete this account' : 'Remove their access'}
        >
          {local ? 'Delete' : 'Remove'}
        </button>
      </td>
    </tr>
  );
}

/**
 * The inline password reset, opened under the row it belongs to.
 *
 * It does not ask for the current password: an administrator resetting
 * somebody else's does not have it, and the self-service change that does ask
 * lives in Settings > Access.
 */
function PasswordRow({
  username,
  busy,
  onCancel,
  onSubmit,
}: {
  username: string;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (password: string) => void;
}) {
  const [value, setValue] = useState('');

  return (
    <tr className="password-row">
      <td colSpan={4}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit(value);
          }}
        >
          <label htmlFor={`pw-${username}`}>New password for {username}</label>
          <input
            id={`pw-${username}`}
            type="password"
            value={value}
            autoComplete="new-password"
            autoFocus
            disabled={busy}
            onChange={(event) => setValue(event.target.value)}
          />
          <button type="submit" className="btn primary small" disabled={busy}>
            Set password
          </button>
          <button type="button" className="btn ghost small" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
          <span className="hint">
            At least {MIN_PASSWORD_LENGTH} characters. Signs them out everywhere.
          </span>
        </form>
      </td>
    </tr>
  );
}
